import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatCents } from "@/lib/money";
import { ChevronLeft, ChevronRight, Pencil, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function currentMonthKey() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftMonth(key, delta) {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function MoneyBudgets() {
  const [month, setMonth] = useState(currentMonthKey());
  const [summary, setSummary] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");

  const load = useCallback(async () => {
    const r = await api.get(`/money/budgets/summary?month=${month}`);
    setSummary(r.data);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditing(row.category_id);
    // For weekly categories the stored value is per-week; for monthly it's per-month.
    setEditValue(((row.base_budget_cents || 0) / 100).toFixed(0));
  };
  const saveEdit = async (row) => {
    const cents = Math.max(0, Math.round(parseFloat(editValue || "0") * 100));
    try {
      await api.patch(`/money/categories/${row.category_id}`, { monthly_budget_cents: cents });
      setEditing(null);
      toast.success("Budget updated");
      await load();
    } catch { toast.error("Update failed"); }
  };

  if (!summary) return <div className="text-sm text-surface-onVariant">Loading budgets…</div>;

  const overallPct = summary.total_budget_cents
    ? Math.min(100, (summary.total_spent_cents / summary.total_budget_cents) * 100) : 0;

  return (
    <div data-testid="money-budgets" className="space-y-6">
      {/* Month switcher + totals */}
      <div className="rounded-hero bg-surface-container p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              data-testid="budget-prev-month"
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
              className="p-2 rounded-full hover:bg-primary/10 active:scale-95 transition-[transform,background-color] duration-200 ease-md text-surface-onVariant"
              aria-label="Previous month"
            ><ChevronLeft className="w-4 h-4" /></button>
            <div className="text-lg font-medium text-surface-on min-w-[10rem] text-center" data-testid="budget-month">{monthLabel(summary.month)}</div>
            <button
              data-testid="budget-next-month"
              onClick={() => setMonth((m) => shiftMonth(m, +1))}
              className="p-2 rounded-full hover:bg-primary/10 active:scale-95 transition-[transform,background-color] duration-200 ease-md text-surface-onVariant"
              aria-label="Next month"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-surface-onVariant">Budgeted / Spent / Remaining</div>
            <div className="text-sm mt-1">
              <span className="text-surface-on font-medium">{formatCents(summary.total_budget_cents)}</span>
              <span className="text-surface-onVariant"> · </span>
              <span className="text-surface-on font-medium">{formatCents(summary.total_spent_cents)}</span>
              <span className="text-surface-onVariant"> · </span>
              <span className="text-primary font-medium">{formatCents(summary.total_remaining_cents)}</span>
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-surface-containerHigh rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all duration-500 ease-md" style={{ width: `${overallPct}%` }} />
        </div>
      </div>

      {/* Category rows */}
      <div className="rounded-hero bg-surface-container overflow-hidden">
        <div className="px-6 py-3 text-xs uppercase tracking-widest text-surface-onVariant border-b border-outline-variant">
          {summary.weeks_in_month}-week month · weekly categories scaled ×{summary.weeks_in_month}
        </div>
        <ul className="divide-y divide-outline-variant" data-testid="budget-rows">
          {summary.rows.map((row) => {
            const pct = row.budget_cents ? Math.min(150, (row.spent_cents / row.budget_cents) * 100) : 0;
            const tone = row.over ? "bg-danger" : pct > 85 ? "bg-tertiary" : "bg-primary";
            const isEditing = editing === row.category_id;
            const isWeekly = row.budget_type === "weekly";
            return (
              <li key={row.category_id} data-testid={`budget-row-${row.category_id}`} className="px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
                    <div className="text-sm font-medium text-surface-on truncate">{row.name}</div>
                    {isWeekly && (
                      <span className="text-[10px] font-medium tracking-wider uppercase bg-tertiary-container text-tertiary-onContainer px-2 py-0.5 rounded-full" data-testid={`badge-weekly-${row.category_id}`}>
                        Weekly ×{summary.weeks_in_month}
                      </span>
                    )}
                    {row.over && (
                      <span className="text-[10px] font-medium tracking-wider uppercase bg-danger-container text-danger px-2 py-0.5 rounded-full">
                        Over
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-sm text-surface-onVariant text-right">
                      <div><span className="text-surface-on font-medium tabular-nums">{formatCents(row.spent_cents)}</span> of </div>
                    </div>
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <div className="flex items-center bg-surface-containerHigh rounded-t-lg border-b-2 border-primary px-2 py-1">
                          <span className="text-surface-onVariant text-sm mr-1">$</span>
                          <input
                            data-testid={`budget-input-${row.category_id}`}
                            type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)}
                            className="bg-transparent outline-none w-24 text-sm text-surface-on"
                            autoFocus
                          />
                          <span className="text-surface-onVariant text-xs ml-1">/{isWeekly ? "wk" : "mo"}</span>
                        </div>
                        <button data-testid={`budget-save-${row.category_id}`} onClick={() => saveEdit(row)} className="p-1.5 rounded-full bg-primary text-primary-foreground hover:brightness-110 transition-colors">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditing(null)} className="p-1.5 rounded-full hover:bg-primary/10">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        data-testid={`budget-edit-${row.category_id}`}
                        onClick={() => startEdit(row)}
                        className="group inline-flex items-center gap-1 text-sm text-surface-onVariant hover:text-primary transition-colors duration-200 ease-md min-w-[8rem] justify-end"
                      >
                        <span className="tabular-nums font-medium">{formatCents(row.budget_cents)}</span>
                        {isWeekly && <span className="text-xs text-surface-onVariant">({formatCents(row.base_budget_cents)}/wk)</span>}
                        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 h-2 bg-surface-containerHigh rounded-full overflow-hidden">
                  <div className={cn("h-full transition-all duration-500 ease-md", tone)} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <div className="mt-1 flex justify-between text-xs text-surface-onVariant">
                  <span>{row.pct}%</span>
                  <span>{formatCents(row.remaining_cents)} left</span>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
